import React, { useState, useCallback } from "react";
import {
  MotifButton,
  MotifButtonGroup,
  MotifDropdown,
  MotifDropdownItem,
  MotifDropdownPortal,
  MotifFormField,
  MotifIcon,
} from "@ey-xd/motif-react";
import { IconoirNavArrowDown, IconoirMoreVert } from "@ey-xd/motif-icon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setShowBasicSeach } from "../../../claims/claimsSlice";
import { useNavigate } from "react-router-dom";
import {
  useExportAdvanceSearchMutation,
  useLazyExportDocumentQuery,
} from "@/services/exports/exportsReducer";
import UpdateStatus from "./UpdateStatus";
import SearchInput from "@/components/SearchInput/SearchInput";

import {
  commonFormateName,
  havingAddPermission,
  havingEditPermission,
} from "@/utility/common";
import {
  CREATE_BUTTON_VARIANT,
  CLOSE_BUTTON_VARIANT,
  getCommonCloseImg,
} from "@/components/Images/CommonImages";

interface TitleComplementProps {
  caseId: string;
  onSearch: (searchValue: string) => void;
  searchQuery: string;
  handleQuickSearchClear: () => void;
  isBasicSearch: boolean;
  handleClearBasicSearch: () => void;
  setShowAdvanceSearch: (isVisible: boolean) => void;
  isAdvanceSearch: boolean;
  showAdvanceSearch: boolean;
  showBasicSearch: boolean;
  advanceSearchPayload: any;
  basicSearchPayload: any;
  handleEditSearch: () => void;
  selectedClaimIds: any;
  clearSelectedClaimIds?: (closeTrigger: boolean) => void;
  allClaimsSelected: boolean;
  isAdvanceSearchActive: boolean;
  isBasicSearchActive: boolean;
  isSearchActive: boolean;
  paginationObj: any;
  Claim: string[];
  ImportJob: string[];
  onBulkClaimCreateSuccess: () => void;
  setErrorMessage: (message: string) => void;
  setShowBulkUploadModal: (isVisible: boolean) => void;
  setShowBulkStageAttachmentModal: (isVisible: boolean) => void;
  setShowUpdateStatusMessage?: any;
  setBulkImportType: (type: "stage" | "mapping") => void;
  setTemplateDownloadError: (message: string) => void;
}

const TitleComplement: React.FC<TitleComplementProps> = ({
  caseId,
  onSearch,
  searchQuery,
  handleQuickSearchClear,
  isBasicSearch,
  handleClearBasicSearch,
  setShowAdvanceSearch,
  isAdvanceSearch,
  showAdvanceSearch,
  showBasicSearch,
  advanceSearchPayload,
  basicSearchPayload,
  handleEditSearch,
  selectedClaimIds,
  clearSelectedClaimIds = () => {},
  allClaimsSelected,
  isAdvanceSearchActive,
  isBasicSearchActive,
  isSearchActive,
  paginationObj,
  Claim,
  ImportJob,
  onBulkClaimCreateSuccess,
  setShowBulkUploadModal,
  setErrorMessage,
  setShowBulkStageAttachmentModal,
  setShowUpdateStatusMessage,
  setBulkImportType,
  setTemplateDownloadError
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [searchValue, setSearchValue] = useState(searchQuery);
  const [exportAdvanceSearch, { isLoading: advancedSearchEcportLoading }] =
    useExportAdvanceSearchMutation();
  const [exportDocument, { isLoading: exportLoading }] =
    useLazyExportDocumentQuery();
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);

  const [validationError, setValidationError] = useState<string | null>(null);
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );

  let debtorTitleMenu = [
    {
      lable: "pages.claims.claimsTable.moreOptions.export",
      value: "export",
    },
  ];
  if (havingEditPermission(Claim)) {
    debtorTitleMenu.push({
      lable: "pages.claims.claimsTable.moreOptions.bulkStatusUpdate",
      value: "bulkStatusUpdate",
    });
  }
  if (havingEditPermission(ImportJob) && havingEditPermission(Claim)) {
    debtorTitleMenu.push({
      lable: "pages.claims.claimsTable.moreOptions.bulkImport",
      value: "bulkImport",
    });
    debtorTitleMenu.push({
      lable: "pages.claims.claimsTable.moreOptions.bulkStageDocument",
      value: "bulkStageDocument",
    });
    debtorTitleMenu.push({
      lable:
        "pages.claims.claimsTable.moreOptions.bulkSupportDocumentsAttachment",
      value: "bulkSupportDocumentsAttachment",
    });
  }
  // const claimTitleMenu = ["Add New Claim", "Bulk Import"];

  const handleMenuClick = (value: string) => {
    setErrorMessage("");
    setTemplateDownloadError("");
    if (value === "export") {
      if (isAdvanceSearch) {
        exportAdvanceSearch({
          url: "ClaimAdvancedSearch/ExportAdvancedClaims",
          data: advanceSearchPayload,
          fileName: caseShortName
            ? commonFormateName(caseShortName, "claim", abbreviation)
            : "",
        })
          .then((response) => {
            if ("error" in response && "data" in response.error) {
              try {
                const errObj = JSON.parse(response?.error?.data as string);
                setErrorMessage(errObj?.message || errObj.title);
              } catch (error) {
                setErrorMessage("Something went wrong!!");
              }
            }
          })
          .catch((error: any) => {
            setErrorMessage("Something went wrong!!");
          });
        return;
      } else {
        exportDocument(
          isBasicSearch
            ? {
                url: "Claim/ExportBasicClaims",
                ...basicSearchPayload,
                fileName: caseShortName
                  ? commonFormateName(caseShortName, "claim", abbreviation)
                  : "",
              }
            : {
                url: "/Claim/ExportClaims",
                caseId,
                SearchText: searchQuery,
                fileName: caseShortName
                  ? commonFormateName(caseShortName, "claim", abbreviation)
                  : "",
              }
        )
          .then((response) => {
            if (response?.error && "data" in response.error) {
              try {
                const errObj = JSON.parse(response?.error?.data as string);
                setErrorMessage(errObj?.message || errObj.title);
              } catch (error) {
                setErrorMessage("Something went wrong!!");
              }
            }
          })
          .catch((error: any) => {
            setErrorMessage("Something went wrong!!");
          });
        return;
      }
    }

    if (value === "bulkStatusUpdate") {
      if (!validateSelectedClaims(selectedClaimIds)) {
        setValidationError(
          "pages.claims.claimsTable.bulkUpdateStatus.bulkStatusUpdateMessage"
        );
        setShowUpdateStatusModal(true);
      } else {
        setValidationError(null);
        setShowUpdateStatusModal(true);
      }
      return;
    }
    if (value === "bulkImport") {
      setShowBulkUploadModal(true);
      return;
    }
    if (
      value === "bulkStageDocument" ||
      value === "bulkSupportDocumentsAttachment"
    ) {
      if (value === "bulkStageDocument") {
        setBulkImportType("stage");
      } else {
        setBulkImportType("mapping");
      }
      setShowBulkStageAttachmentModal(true);
      return;
    }
  };

  const handleSearchChange = useCallback(
    (event: { target: { value: React.SetStateAction<string> } }) => {
      setSearchValue(event.target.value);
    },
    []
  );

  const validateSelectedClaims = (selectedClaims: any[]) => {
    if (selectedClaims.length === 0) return true;

    const { actionStatusName, claimStageName } = selectedClaims[0];

    for (let i = 1; i < selectedClaims.length; i++) {
      if (
        selectedClaims[i].actionStatusName !== actionStatusName ||
        selectedClaims[i].claimStageName !== claimStageName
      ) {
        return false;
      }
    }

    return true;
  };

  const handleOnSearch = useCallback(() => {
    onSearch(searchValue);
  }, [onSearch, searchValue]);

  const filteredDebtorTitleMenu =
    selectedClaimIds.length > 1
      ? debtorTitleMenu
      : debtorTitleMenu.filter(
          (menuName) => menuName.value !== "bulkStatusUpdate"
        );

  return (
    <>
      <div
        className={
          showAdvanceSearch ? "show-and-hide-quicksearch" : "show-quick-search"
        }
      >
        <MotifFormField>
          {!showAdvanceSearch && !showBasicSearch ? (
            <div style={{ width: "180px" }} className="claims-search-input">
              {" "}
              <SearchInput
                aria-label="Search"
                onChange={handleSearchChange}
                onClear={handleQuickSearchClear}
                onFocusShow={false}
                value={searchQuery}
                onEnter={handleOnSearch}
                placeholder={t(
                  "global.globalPlaceholder.quickSearchPlaceHolder"
                )}
                withOutDebounce={true}
              />
            </div>
          ) : (
            ""
          )}
          <MotifButtonGroup className="claim-search-button-group">
            <MotifButton
              variant="secondary"
              onClick={() => {
                dispatch(setShowBasicSeach(false));
              }}
            >
              {t("global.globalPlaceholder.searchTypesDropdown")}
            </MotifButton>
            <MotifDropdownPortal
              open={showMenu}
              hideArrow={true}
              handleClickOutside={() => setShowMenu(false)}
              trigger={
                <MotifButton
                  type="button"
                  onClick={() => setShowMenu(!showMenu)}
                  aria-label="Dropdown button"
                  aria-haspopup="true"
                  variant="secondary"
                >
                  <MotifIcon
                    aria-label="Dropdown icon"
                    fill="none"
                    iconFunction={IconoirNavArrowDown}
                  />
                </MotifButton>
              }
            >
              <MotifDropdownItem
                onClick={() => {
                  dispatch(setShowBasicSeach(true));
                  setShowAdvanceSearch(false);
                  setShowUpdateStatusMessage({
                    message: "",
                    show: false,
                    type: "",
                  });
                }}
              >
                {t("pages.claims.basicSearch.title")}
              </MotifDropdownItem>
              <MotifDropdownItem
                onClick={() => {
                  dispatch(setShowBasicSeach(false));
                  setShowAdvanceSearch(true);
                  setShowUpdateStatusMessage({
                    message: "",
                    show: false,
                    type: "",
                  });
                }}
              >
                {t("pages.claims.advancedSearch.title")}
              </MotifDropdownItem>
            </MotifDropdownPortal>
          </MotifButtonGroup>

          {isBasicSearch || isAdvanceSearch ? (
            <div className="search-div">
              <MotifButton
                size="small"
                variant={CLOSE_BUTTON_VARIANT}
                onClick={handleClearBasicSearch}
                type="button"
                title="Clear Search"
              >
                {getCommonCloseImg()}
              </MotifButton>
              <MotifButton
                style={{ marginTop: "-46px", marginLeft: "57px" }}
                size="small"
                type="button"
                variant={CREATE_BUTTON_VARIANT}
                onClick={handleEditSearch}
              >
                {t("pages.claims.claimsTable.editSearch")}
              </MotifButton>
            </div>
          ) : (
            ""
          )}
          {havingAddPermission(Claim) && (
            <MotifButton
              className="claim-create-button"
              style={{ marginTop: "1px", marginLeft: "20px", height: "44px" }}
              onClick={() => {
                navigate(`/case/${caseId}/claim/claim-setup`);
              }}
              variant={CREATE_BUTTON_VARIANT}
            >
              {t("pages.claims.claimsTable.createClaim")}
            </MotifButton>
          )}
          <MotifDropdown
            ariaLabelledby="dropdown-trigger-1"
            id="dropdown-1"
            placement="bottom"
            trigger={
              <MotifButton
                aria-label="Search Dropdown"
                id="dropdown-trigger-1"
                onClick={function Ga() {}}
                type="button"
                variant="text"
                loading={advancedSearchEcportLoading || exportLoading}
              >
                <MotifIcon iconFunction={IconoirMoreVert} />
              </MotifButton>
            }
          >
            <ul role="menu">
              {filteredDebtorTitleMenu.map((menuName) => {
                // const isBulkStatusUpdate = menuName === "Bulk Status Update";
                // const isDisabled =
                //   isBulkStatusUpdate && selectedClaimIds.length === 0;
                return (
                  <li role="menuitem" key={menuName.value}>
                    <MotifDropdownItem
                      indexItem={1}
                      onClick={() => handleMenuClick(menuName.value)}
                    >
                      {t(menuName.lable)}
                    </MotifDropdownItem>
                  </li>
                );
              })}
            </ul>
          </MotifDropdown>
        </MotifFormField>
      </div>

      {showUpdateStatusModal && (
        <UpdateStatus
          showModal={true}
          setShowModal={setShowUpdateStatusModal}
          title="pages.claims.claimsTable.bulkUpdateStatus.title"
          onClose={() => {
            setShowUpdateStatusModal(false);
          }}
          isError={validationError ?? ""}
          selectedClaimIds={selectedClaimIds}
          clearSelectedClaimIds={clearSelectedClaimIds}
          isAdvanceSearchActive={isAdvanceSearchActive}
          advanceSearchPayload={advanceSearchPayload}
          isBasicSearchActive={isBasicSearchActive}
          basicSearchPayload={basicSearchPayload}
          isSearchActive={isSearchActive}
          paginationObj={paginationObj}
          allClaimsSelected={allClaimsSelected}
          setShowUpdateStatusMessage={setShowUpdateStatusMessage}
        />
      )}
    </>
  );
};

export default TitleComplement;
